import { useUserStore } from '@/stores/userStore'
import { ApiResponse } from '@/api/types/common'
import { toast } from '@you/ui'

// const BASE_URL = 'http://localhost:3030'
// 使用当前页面的协议，避免混合内容错误（HTTPS 页面不能请求 HTTP 资源）
const BASE_URL = `${window.location.protocol}//ai-api.yichenglink.com`

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

const getHeaders = () => {
  const headers: Record<string, string> = {}
  const { currentUser } = useUserStore.getState()

  console.log('Getting headers:', {
    hasCurrentUser: !!currentUser,
    memberToken: currentUser?.memberToken,
  })

  if (currentUser?.memberToken) {
    headers['memberToken'] = currentUser.memberToken
  }

  return headers
}

interface FetcherOptions extends RequestInit {
  params?: Record<string, string>
  data?: unknown
  showError?: boolean // 是否显示错误提示，默认为 true
  signal?: AbortSignal // 支持 AbortController
}

class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown
  ) {
    super(`${status} ${statusText}`)
    this.name = 'HttpError'
  }
}

// 执行请求的核心函数
async function executeRequest<T>(
  url: string,
  config: RequestInit,
  showError: boolean
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${BASE_URL}${url}`, config)
    const responseData: ApiResponse<T> = await response.json()

    if (!response.ok) {
      const error = new HttpError(
        response.status,
        response.statusText,
        responseData
      )
      if (showError) {
        toast.error(`请求失败: ${response.status} ${response.statusText}`)
      }
      throw error
    }

    return responseData
  } catch (error) {
    // 检查是否是 AbortError（请求被取消）
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('请求被取消:', url)
      throw error
    }

    if (
      error instanceof TypeError &&
      error.message.includes('Failed to fetch')
    ) {
      // 检查是否是证书错误（通常在控制台会有 ERR_CERT_* 错误）
      const isCertError =
        error.message.includes('certificate') ||
        error.message.includes('SSL') ||
        error.message.includes('TLS') ||
        (typeof window !== 'undefined' &&
          window.location.protocol === 'https:' &&
          BASE_URL.startsWith('https://'))

      const message = isCertError
        ? 'SSL 证书验证失败，请联系管理员检查服务器证书配置'
        : '网络连接失败，请稍后再试'

      if (showError) {
        toast.error(message)
      }
      throw new Error(message)
    }
    throw error
  }
}

// 执行文件下载请求的核心函数
async function executeDownloadRequest(
  url: string,
  config: RequestInit,
  showError: boolean
): Promise<{ data: string; filename: string; description?: string }> {
  try {
    const response = await fetch(`${BASE_URL}${url}`, config)

    if (!response.ok) {
      const error = new HttpError(response.status, response.statusText, null)
      if (showError) {
        toast.error(`下载失败: ${response.status} ${response.statusText}`)
      }
      throw error
    }

    // 尝试解析 JSON 响应（标准格式）
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      const responseData = await response.json()

      // 处理业务逻辑错误
      if (responseData.code !== 200) {
        const errorMessage = responseData.msg || '下载失败'
        if (showError) {
          toast.error(errorMessage)
        }
        throw new Error(errorMessage)
      }

      return responseData.data
    } else {
      // 处理文件流响应
      const blob = await response.blob()
      const filename =
        response.headers
          .get('content-disposition')
          ?.split('filename=')[1]
          ?.replace(/"/g, '') || 'download'

      // 将 Blob 转换为 base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const base64 = reader.result as string
          resolve({
            data: base64.split(',')[1], // 移除 data:application/...;base64, 前缀
            filename,
            description: '文件下载',
          })
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    }
  } catch (error) {
    if (
      error instanceof TypeError &&
      error.message.includes('Failed to fetch')
    ) {
      // 检查是否是证书错误
      const isCertError =
        error.message.includes('certificate') ||
        error.message.includes('SSL') ||
        error.message.includes('TLS') ||
        (typeof window !== 'undefined' &&
          window.location.protocol === 'https:' &&
          BASE_URL.startsWith('https://'))

      const message = isCertError
        ? 'SSL 证书验证失败，请联系管理员检查服务器证书配置'
        : '网络连接失败，请稍后再试'

      if (showError) {
        toast.error(message)
      }
      throw new Error(message)
    }
    throw error
  }
}

async function baseFetch<T>(
  url: string,
  method: HttpMethod = 'GET',
  options: FetcherOptions = {}
): Promise<T> {
  const { params, data, showError = true, signal, ...customConfig } = options

  // 处理查询参数
  const queryString = params ? `?${new URLSearchParams(params).toString()}` : ''

  // 获取默认 headers
  const headers: Record<string, string> = {
    ...getHeaders(),
  }

  // 如果不是 FormData，添加 Content-Type
  if (!(data instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  // 合并自定义 headers
  if (customConfig.headers) {
    Object.assign(headers, customConfig.headers)
  }

  const config: RequestInit = {
    method,
    headers,
    signal, // 添加 AbortSignal 支持
    ...customConfig,
  }

  if (data) {
    if (data instanceof FormData) {
      config.body = data
      // FormData 不需要设置 Content-Type，但要保留其他 headers
      delete headers['Content-Type']
    } else {
      config.body = JSON.stringify(data)
    }
  }

  console.log('Request config:', {
    url: `${BASE_URL}${url}${queryString}`,
    method,
    headers,
    isFormData: data instanceof FormData,
    hasSignal: !!signal,
  })

  const responseData = await executeRequest<T>(
    `${url}${queryString}`,
    config,
    showError
  )

  if (responseData.code === 1) {
    window.location.href = '/login'
  }

  // 处理业务逻辑错误
  if (responseData.code !== 200) {
    const errorMessage = responseData.msg || '操作失败'
    if (showError) {
      toast.error(errorMessage)
    }
    throw new Error(errorMessage)
  }

  return responseData.data
}

// 文件下载专用方法
async function baseDownload(
  url: string,
  options: FetcherOptions = {}
): Promise<{ data: string; filename: string; description?: string }> {
  const { params, showError = true, ...customConfig } = options

  // 处理查询参数
  const queryString = params ? `?${new URLSearchParams(params).toString()}` : ''

  // 获取默认 headers
  const headers: Record<string, string> = {
    ...getHeaders(),
  }

  // 合并自定义 headers
  if (customConfig.headers) {
    Object.assign(headers, customConfig.headers)
  }

  const config: RequestInit = {
    method: 'GET',
    headers,
    ...customConfig,
  }

  console.log('Download request config:', {
    url: `${BASE_URL}${url}${queryString}`,
    headers,
  })

  return executeDownloadRequest(`${url}${queryString}`, config, showError)
}

export const fetcher = {
  get: <T>(url: string, options?: FetcherOptions) =>
    baseFetch<T>(url, 'GET', options),
  post: <T>(url: string, data?: unknown, options?: FetcherOptions) =>
    baseFetch<T>(url, 'POST', { ...options, data }),
  put: <T>(url: string, data?: unknown, options?: FetcherOptions) =>
    baseFetch<T>(url, 'PUT', { ...options, data }),
  delete: <T>(url: string, options?: FetcherOptions) =>
    baseFetch<T>(url, 'DELETE', options),
  download: (url: string, options?: FetcherOptions) =>
    baseDownload(url, options),
}
